import PageLayout from "@/components/PageLayout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Workflow, Zap, GitBranch, Clock, CheckCircle, AlertTriangle, Database, Truck, Package } from "lucide-react";
import { Link } from "react-router-dom";
import { useSEO } from "@/hooks/useSEO";
import RelatedFeaturesSection from "@/components/RelatedFeaturesSection";

const OmmniFlow = () => {
  useSEO({
    title: 'Workflow Automation Platform | OmmniFlow - No-Code Commerce Automation',
    description: 'Automate commerce operations with OmmniFlow. Build visual workflows, trigger actions on events, and streamline processes without coding. Increase efficiency across your business.',
    keywords: 'workflow automation, commerce automation, no-code automation, business process automation, workflow builder',
    canonical: 'https://ommni.io/features/ommni-flow',
  });

  const features = [
    {
      icon: Workflow,
      title: "Visual Workflow Builder",
      description: "Design complex automation workflows with an intuitive drag-and-drop interface. No coding required.",
    },
    {
      icon: Zap,
      title: "Real-Time Triggers",
      description: "Automate actions based on inventory changes, order updates, price modifications, and more.",
    },
    {
      icon: GitBranch,
      title: "Conditional Logic",
      description: "Create sophisticated workflows with branching paths, filters, and conditional operations.",
    },
    {
      icon: Clock,
      title: "Scheduled Actions",
      description: "Set up time-based workflows to run reports, update products, or sync data on your schedule.",
    },
    {
      icon: CheckCircle,
      title: "Pre-built Templates",
      description: "Start fast with ready-made workflow templates for common e-commerce operations.",
    },
    {
      icon: AlertTriangle,
      title: "Error Handling",
      description: "Built-in monitoring and alerts keep you informed when workflows need attention.",
    },
  ];

  const useCases = [
    {
      title: "Inventory Automation",
      description: "Automatically update stock levels across all channels when inventory changes in your warehouse.",
    },
    {
      title: "Order Routing",
      description: "Route orders to the optimal fulfillment center based on location, inventory, or custom rules.",
    },
    {
      title: "Price Synchronization",
      description: "Keep pricing consistent across all sales channels with automated updates.",
    },
    {
      title: "Product Enrichment",
      description: "Automatically enhance product data with translations, SEO optimization, or category assignments.",
    },
    {
      title: "Notification Workflows",
      description: "Send alerts to teams or customers when specific conditions are met.",
    },
    {
      title: "Data Transformation",
      description: "Transform and map data between different systems and formats automatically.",
    },
  ];

  return (
    <PageLayout>
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "SoftwareApplication",
          "name": "OmmniFlow - Workflow Automation Platform",
          "applicationCategory": "BusinessApplication",
          "operatingSystem": "Web",
          "description": "Powerful workflow automation built right into your commerce operations platform. Automate repetitive tasks, connect systems, and streamline your operations without writing code.",
          "offers": {
            "@type": "Offer",
            "price": "500",
            "priceCurrency": "USD",
            "description": "Starting at $500/month"
          },
          "provider": {
            "@type": "Organization",
            "name": "Ommni",
            "url": "https://ommni.io"
          },
          "featureList": [
            "Visual Workflow Builder",
            "Real-Time Triggers",
            "Conditional Logic",
            "Scheduled Actions",
            "Pre-built Templates",
            "Error Handling"
          ]
        })}
      </script>
      <div className="px-4 pb-24">
        <div className="container mx-auto max-w-7xl">
          {/* Hero Section */}
          <div className="text-center mb-16">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-primary/10 mb-6">
              <Workflow className="h-10 w-10 text-primary" />
            </div>
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              Ommni Flow
            </h1>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-8">
              Powerful workflow automation built right into your commerce operations platform. 
              Automate repetitive tasks, connect systems, and streamline your operations without writing code.
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Button variant="cta" size="lg" asChild>
                <Link to="/free-trial">Start Automating</Link>
              </Button>
              <Button variant="outline" size="lg" asChild>
                <Link to="/contact">Book a Demo</Link>
              </Button>
            </div>
          </div>

          {/* Features Grid */}
          <div className="mb-20">
            <h2 className="text-3xl font-bold text-center mb-12">Core Capabilities</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {features.map((feature, index) => (
                <Card key={index} className="p-8 gradient-card">
                  <div className="mb-4">
                    <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                      <feature.icon className="h-6 w-6 text-primary" />
                    </div>
                  </div>
                  <h3 className="text-xl font-bold mb-3">{feature.title}</h3>
                  <p className="text-muted-foreground">{feature.description}</p>
                </Card>
              ))}
            </div>
          </div>

          {/* Use Cases Section */}
          <div className="mb-20">
            <h2 className="text-3xl font-bold text-center mb-12">Common Use Cases</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {useCases.map((useCase, index) => (
                <Card key={index} className="p-6 hover:shadow-lg transition-shadow">
                  <h3 className="text-lg font-semibold mb-2">{useCase.title}</h3>
                  <p className="text-sm text-muted-foreground">{useCase.description}</p>
                </Card>
              ))}
            </div>
          </div>

          {/* Related Features */}
          <RelatedFeaturesSection
            features={[
              {
                icon: Database,
                title: "Product Information Management",
                description: "Automate product data enrichment and publishing workflows with PIM.",
                link: "/features/pim",
              },
              {
                icon: Truck,
                title: "Order Management",
                description: "Create automated order routing and fulfillment workflows.",
                link: "/features/dom",
              },
              {
                icon: Package,
                title: "Inventory Management",
                description: "Automate inventory updates and low stock alerts across channels.",
                link: "/features/inventory",
              },
            ]}
          />

          {/* Integration Section */}
          <Card className="p-12 bg-primary/5 border-2 border-primary/20 text-center">
            <h2 className="text-3xl font-bold mb-6">
              Works With All Your Systems
            </h2>
            <p className="text-foreground/80 max-w-2xl mx-auto mb-8 text-lg">
              Ommni Flow integrates seamlessly with your existing tools and platforms. 
              Connect Shopify, Amazon, ERP systems, warehouses, marketing tools, and more. 
              Build workflows that span your entire tech stack.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Button variant="cta" size="lg" asChild>
                <Link to="/integrations">View All Integrations</Link>
              </Button>
              <Button variant="outline" size="lg" asChild>
                <Link to="/pricing">See Pricing</Link>
              </Button>
            </div>
          </Card>
        </div>
      </div>
    </PageLayout>
  );
};

export default OmmniFlow;
